import { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { logger } from '../../utils/logger';
import { reportErrorBoundaryError } from '../../utils/error-monitoring';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

const ErrorContainer = styled.div`
  padding: 2rem;
  text-align: center;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  margin: 1rem;
`;

const ErrorTitle = styled.h2`
  color: #ef4444;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  color: #6b7280;
  margin-bottom: 1rem;
`;

const RetryButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #2563eb;
  }
`;

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error('ErrorBoundary caught an error', { error, errorInfo });
    
    // 本番環境では外部エラー監視サービスに送信
    reportErrorBoundaryError(error, { 
      componentStack: errorInfo.componentStack || null 
    }).catch((reportError) => {
      console.error('Failed to report error to monitoring service:', reportError);
    });
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorContainer>
          <ErrorTitle>申し訳ございません</ErrorTitle>
          <ErrorMessage>
            予期しないエラーが発生しました。
            {this.state.error?.message && (
              <><br />エラー詳細: {this.state.error.message}</>
            )}
          </ErrorMessage>
          <RetryButton onClick={this.handleRetry}>
            再試行
          </RetryButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;